import type { SportsbookSummaryFilterMetaDto } from "./sportsbookAnalyticsSummaryTypes";
import { AppStrings } from "../../utils/constants/AppStrings";

export type FilterOption = { value: string; label: string };

export interface ReportingSummaryFilterGroup {
  options: FilterOption[];
  defaultSelected: string[];
  multiSelect: boolean;
}

export interface ReportingSummaryFilterOptions {
  money_type: ReportingSummaryFilterGroup;
  bet_status: ReportingSummaryFilterGroup;
  bet_type: ReportingSummaryFilterGroup;
  sport: ReportingSummaryFilterGroup;
  league: ReportingSummaryFilterGroup;
  odds_provider: ReportingSummaryFilterGroup;
  state: ReportingSummaryFilterGroup;
}

export type ReportingSummaryFilterKey = keyof ReportingSummaryFilterOptions;

export type ReportingSummaryFiltersValue = Record<
  ReportingSummaryFilterKey,
  string[]
>;

export const REPORTING_SUMMARY_FILTER_KEYS: ReportingSummaryFilterKey[] = [
  "money_type",
  "bet_status",
  "bet_type",
  "sport",
  "league",
  "odds_provider",
  "state",
];

const EQ_FILTER_KEYS = new Set<ReportingSummaryFilterKey>([
  "sport",
  "league",
  "odds_provider",
  "state",
]);

function filterParamKey(key: ReportingSummaryFilterKey): string {
  return EQ_FILTER_KEYS.has(key) ? `${key}[eq]` : `${key}[in]`;
}

/** Curated label overrides for well-known tokens. Falls back to title-case. */
const HUMAN_LABEL_OVERRIDES: Record<string, string> = {
  rebet_cash: "Rebet Cash",
  rebet_free_cash: "Rebet Free Cash",
  casino_cash: "Casino Cash",
  pikkem_cash: "Pikkem Cash",
  cash: "Cash",
  free_cash: "Free Cash",
  cashed_out: "Cashed Out",
  lost: "Lost",
  open: "Open",
  won: "Won",
  nba: "NBA",
  nfl: "NFL",
  mlb: "MLB",
  nhl: "NHL",
  epl: "English Premier League",
  atp: "ATP Tour",
  nj: "New Jersey",
  ny: "New York",
  pa: "Pennsylvania",
  mi: "Michigan",
  co: "Colorado",
};

function titleCase(token: string): string {
  return token
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function sameStringSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const bValues = new Set(b);
  return new Set(a).size === bValues.size && a.every((value) => bValues.has(value));
}

/**
 * Produces a human-readable label for a raw backend token. Callers may pass
 * the filter key for future context-sensitive overrides (not yet used).
 */
export function humanizeFilterValue(
  token: string,
  _key?: ReportingSummaryFilterKey,
): string {
  if (!token) return "";
  const override = HUMAN_LABEL_OVERRIDES[token];
  if (override) return override;
  return titleCase(token);
}

function buildGroup(
  meta: SportsbookSummaryFilterMetaDto | undefined,
  key: ReportingSummaryFilterKey,
): ReportingSummaryFilterGroup {
  if (!meta) return { options: [], defaultSelected: [], multiSelect: true };
  const effectiveDefaults = meta.selected_values?.length
    ? meta.selected_values
    : meta.default_values;
  const defaultsCoverAllOptions =
    meta.options.length > 0 &&
    sameStringSet(meta.default_values ?? [], meta.options);
  const effectiveDefaultsContainAll = effectiveDefaults?.includes("all") ?? false;
  const hasAllOption =
    defaultsCoverAllOptions ||
    meta.default_values?.includes("all") ||
    effectiveDefaultsContainAll;
  const selectsAllOptions =
    effectiveDefaultsContainAll ||
    (defaultsCoverAllOptions &&
      sameStringSet(effectiveDefaults ?? [], meta.options));
  const optionValues =
    hasAllOption && !meta.options.includes("all")
      ? ["all", ...meta.options]
      : meta.options;
  const options = optionValues.map((value) => ({
    value,
    label: value === "all" ? AppStrings.ALL : humanizeFilterValue(value, key),
  }));
  return {
    options,
    defaultSelected: selectsAllOptions ? ["all"] : [...(effectiveDefaults ?? [])],
    multiSelect: meta.multi_select,
  };
}

/**
 * Derives the filter panel's `{ options, defaultSelected }` structure from the
 * `meta.filters` block of a summary response. Missing keys collapse to empty
 * groups so the filter panel can still render without crashing.
 *
 * Returns `null` when `meta.filters` itself is missing or empty — the caller
 * uses that signal to keep the filter panel in its loading state.
 */
export function filtersMetaToOptions(
  filters: SportsbookSummaryFilterMetaDto[] | undefined,
): ReportingSummaryFilterOptions | null {
  if (!filters || filters.length === 0) return null;
  const byKey = new Map<string, SportsbookSummaryFilterMetaDto>(
    filters.map((f) => [f.key, f]),
  );
  return REPORTING_SUMMARY_FILTER_KEYS.reduce<ReportingSummaryFilterOptions>(
    (acc, key) => {
      acc[key] = buildGroup(byKey.get(key), key);
      return acc;
    },
    {} as ReportingSummaryFilterOptions,
  );
}

export function reportingSummaryFiltersToSearchParams(
  filters: ReportingSummaryFiltersValue | null,
): URLSearchParams {
  const params = new URLSearchParams();
  if (!filters) return params;

  REPORTING_SUMMARY_FILTER_KEYS.forEach((key) => {
    const values = filters[key];
    if (values.length > 0) {
      params.set(filterParamKey(key), values.join(","));
    }
  });
  return params;
}

export function reportingSummaryFiltersFromSearchParams(
  params: URLSearchParams,
): ReportingSummaryFiltersValue {
  return REPORTING_SUMMARY_FILTER_KEYS.reduce<ReportingSummaryFiltersValue>(
    (filters, key) => {
      filters[key] = (params.get(filterParamKey(key)) ?? "")
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
      return filters;
    },
    {} as ReportingSummaryFiltersValue,
  );
}
